import React, {
  createContext,
  FunctionComponent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useGetAllFeatureFlagsLaunchDarkly, useGetFeatureFlagLaunchDarkly } from "@coherehealth/core-platform-api";
import { store } from "../util";

export type LDFlagValue = boolean | string | number | { [key: string]: any } | any[] | null;

export interface LDFlagEntry {
  value: LDFlagValue;
  type?: string;
  kind?: string;
  errorKind?: string | null;
}

interface LDContextValue {
  flags: Record<string, LDFlagEntry>;
  loading: boolean;
  initialized: boolean;
  refetch: () => Promise<void>;
}

const LD_CACHE_STORAGE_KEY = "cohere-launchdarkly-bulk-flags";
const DEFAULT_TTL_MS = 60 * 1000;

const MISSING_PROVIDER_SENTINEL: LDContextValue = {
  flags: {},
  loading: false,
  initialized: false,
  refetch: async () => {},
};

const LDFlagsContext = createContext<LDContextValue>(MISSING_PROVIDER_SENTINEL);

const warnedFlags = new Set<string>();
function warnIfNoProvider(name: string, ctx: LDContextValue) {
  if (process.env.NODE_ENV === "production") {
    return;
  }
  if (ctx !== MISSING_PROVIDER_SENTINEL) {
    return;
  }
  if (warnedFlags.has(name)) {
    return;
  }
  warnedFlags.add(name);
  console.warn(
    `useLDFlagBulkCached("${name}") called without a LaunchDarklyBulkCachedFlagsProvider in the tree. ` +
      `Returning default value. Mount LaunchDarklyBulkCachedFlagsProvider at your app root to enable LaunchDarkly flags.`
  );
}

interface CachedPayload {
  flags: Record<string, LDFlagEntry>;
  fetchedAt: number;
}

function readCachedFlags(ttlMs: number): Record<string, LDFlagEntry> | null {
  const entry: CachedPayload | undefined = store.get(LD_CACHE_STORAGE_KEY);
  if (!entry) {
    return null;
  }
  if (Date.now() - entry.fetchedAt > ttlMs) {
    return null;
  }
  return entry.flags;
}

function writeCachedFlags(flags: Record<string, LDFlagEntry>) {
  const entry: CachedPayload = { flags, fetchedAt: Date.now() };
  store.set(LD_CACHE_STORAGE_KEY, entry);
}

interface LaunchDarklyBulkCachedFlagsProviderProps {
  ttlMs?: number;
  disabled?: boolean;
  /**
   * When false, the mount fetch is deferred until this flips true. On SSO landings the access
   * token isn't available on mount, so fetching immediately 401s and commits flag defaults for
   * the rest of the session. Defaults to true so non-auth-gated callers (stories, tests) behave
   * as before. See KOZA-752.
   */
  authReady?: boolean;
  children: ReactNode;
}

/**
 * Bulk-cached LaunchDarkly flags provider.
 *
 * Fetches all LaunchDarkly flags for the authenticated user in a single request
 * to `/launchDarkly/allFlagsState` on mount and caches the result in
 * `localStorage` under `cohere-launchdarkly-bulk-flags`. While the cache is
 * within `ttlMs` (default 1 minute), subsequent mounts read synchronously from
 * the cache and skip the network request entirely.
 *
 * Read individual flag values via `useLDFlagBulkCached`.
 */
export const LaunchDarklyBulkCachedFlagsProvider: FunctionComponent<LaunchDarklyBulkCachedFlagsProviderProps> = ({
  ttlMs = DEFAULT_TTL_MS,
  disabled = false,
  authReady = true,
  children,
}) => {
  const [flags, setFlags] = useState<Record<string, LDFlagEntry>>(() => readCachedFlags(ttlMs) ?? {});
  const [loading, setLoading] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(() => readCachedFlags(ttlMs) !== null);

  const { mutate } = useGetAllFeatureFlagsLaunchDarkly({});

  const inFlightRef = useRef<boolean>(false);

  const fetchFlags = useCallback(async () => {
    if (inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setLoading(true);
    try {
      const resp = await mutate({ context: null });
      const next: Record<string, LDFlagEntry> = {};
      (resp || []).forEach((f) => {
        if (!f.key) {
          return;
        }
        next[f.key] = {
          value: f.value ?? null,
          type: f.type,
          kind: f.kind,
          errorKind: f.errorKind,
        };
      });
      setFlags(next);
      writeCachedFlags(next);
    } catch (error) {
      console.error("LaunchDarkly bulk flag fetch failed:", error);
    } finally {
      setInitialized(true);
      setLoading(false);
      inFlightRef.current = false;
    }
  }, [mutate]);

  useEffect(() => {
    if (disabled) {
      setInitialized(true);
      return;
    }
    const cached = readCachedFlags(ttlMs);
    if (cached) {
      setFlags(cached);
      setInitialized(true);
      return;
    }
    // Wait for auth before fetching. On SSO landings the access token isn't ready on mount, so
    // firing now 401s, logs "LaunchDarkly bulk flag fetch failed", and leaves flags at their
    // defaults for the session. When authReady flips true this effect re-runs and the fetch
    // succeeds with real values. See KOZA-752.
    if (!authReady) {
      return;
    }
    fetchFlags();
  }, [ttlMs, disabled, authReady, fetchFlags]);

  const refetch = useCallback(async () => {
    await fetchFlags();
  }, [fetchFlags]);

  const value = useMemo<LDContextValue>(
    () => ({ flags, loading, initialized, refetch }),
    [flags, loading, initialized, refetch]
  );

  return <LDFlagsContext.Provider value={value}>{children}</LDFlagsContext.Provider>;
};

/**
 * Reads a single flag from the bulk-cached LaunchDarkly snapshot loaded by
 * `LaunchDarklyBulkCachedFlagsProvider`. Returns `defaultValue` if the flag
 * is missing from the snapshot, evaluated to ERROR, or the provider is not
 * mounted in the tree.
 *
 * Use this for flags whose evaluation depends only on the authenticated user
 * context and that can tolerate up to `ttlMs` (default 1 minute) of staleness.
 */
export function useLDFlagBulkCached<T extends LDFlagValue = boolean>(name: string, defaultValue: T): T {
  const ctx = useContext(LDFlagsContext);
  warnIfNoProvider(name, ctx);
  const entry = ctx.flags[name];
  if (!entry || entry.kind === "ERROR") {
    return defaultValue;
  }
  if (entry.value === undefined || entry.value === null) {
    return defaultValue;
  }
  return entry.value as T;
}

export function useLDFlagsBulkCachedInitialized(): boolean {
  return useContext(LDFlagsContext).initialized;
}

export function useLDFlagsBulkCachedLoading(): boolean {
  return useContext(LDFlagsContext).loading;
}

export function useRefetchLDFlagsBulkCached(): () => Promise<void> {
  return useContext(LDFlagsContext).refetch;
}

// ---------------------------------------------------------------------------
// Targeted (per-context) LaunchDarkly flags
// ---------------------------------------------------------------------------
//
// `LaunchDarklyBulkCachedFlagsProvider` evaluates flags against the user
// context only. Flags whose targeting rules depend on per-request attributes
// (e.g. healthPlanName, delegatedVendorName) need to call the single-flag
// endpoint with that context.
//
// `LaunchDarklyTargetedFlagsProvider` owns one `useGetFeatureFlagLaunchDarkly`
// mutate at the app root, so its AbortController is never torn down by a
// consumer unmount/strict-mode cycle (which is what makes the previous
// per-consumer hook flake — `restful-react`'s `mutate` silently resolves to
// `undefined` when its host component unmounts mid-fetch).
//
// Reads go through `useLaunchDarklyTargetedFlag(name, context)`. The provider
// dedupes in-flight requests by cache key and notifies all subscribers when a
// fetch resolves.

export interface TargetedFlagContext {
  healthPlanName: string;
  delegatedVendorName?: string;
  opsGroup?: string;
}

interface TargetedCacheEntry {
  value: boolean;
  fetchedAt: number;
  isLoading: boolean;
  promise?: Promise<boolean>;
  subscribers: Set<(value: boolean) => void>;
}

interface TargetedFlagsContextValue {
  evaluate: (name: string, ctx?: TargetedFlagContext) => Promise<boolean>;
  subscribe: (name: string, ctx: TargetedFlagContext | undefined, listener: (v: boolean) => void) => () => void;
  getEntry: (name: string, ctx?: TargetedFlagContext) => TargetedCacheEntry | undefined;
  ttlMs: number;
}

const TARGETED_DEFAULT_TTL_MS = 60 * 1000;

const TargetedFlagsContext = createContext<TargetedFlagsContextValue | null>(null);

function targetedCacheKey(name: string, ctx?: TargetedFlagContext): string {
  return `${name}:${ctx?.healthPlanName ?? ""}:${ctx?.delegatedVendorName ?? ""}:${ctx?.opsGroup ?? ""}`;
}

function getOrCreateEntry(cache: Map<string, TargetedCacheEntry>, key: string): TargetedCacheEntry {
  let entry = cache.get(key);
  if (!entry) {
    entry = { value: false, fetchedAt: 0, isLoading: false, subscribers: new Set() };
    cache.set(key, entry);
  }
  return entry;
}

interface LaunchDarklyTargetedFlagsProviderProps {
  ttlMs?: number;
  /**
   * When false, `evaluate` short-circuits to the current (default) value without hitting the flag
   * endpoint, so no request fires during the SSO cold window. `evaluate`'s identity changes when
   * this flips true, so subscribed consumers re-evaluate with a real token. See KOZA-752.
   */
  authReady?: boolean;
  children: ReactNode;
}

export const LaunchDarklyTargetedFlagsProvider: FunctionComponent<LaunchDarklyTargetedFlagsProviderProps> = ({
  ttlMs = TARGETED_DEFAULT_TTL_MS,
  authReady = true,
  children,
}) => {
  const { mutate } = useGetFeatureFlagLaunchDarkly({});
  const cacheRef = useRef<Map<string, TargetedCacheEntry>>(new Map());

  const evaluate = useCallback(
    (name: string, ctx?: TargetedFlagContext): Promise<boolean> => {
      const key = targetedCacheKey(name, ctx);
      const entry = getOrCreateEntry(cacheRef.current, key);

      if (!entry.isLoading && entry.fetchedAt > 0 && Date.now() - entry.fetchedAt < ttlMs) {
        return Promise.resolve(entry.value);
      }
      if (entry.promise) {
        return entry.promise;
      }
      // Wait for auth before hitting the flag endpoint. During the SSO cold window the token isn't
      // ready, so the request 401s, logs "Error fetching feature flag", and commits a default.
      // Return the current value without stamping fetchedAt so the read retries once authReady
      // flips true (evaluate's identity changes, re-running subscribed consumers). See KOZA-752.
      if (!authReady) {
        return Promise.resolve(entry.value);
      }

      entry.isLoading = true;
      const promise = (async () => {
        try {
          const response = await mutate({ flagName: name, context: ctx });
          if (response === undefined) {
            // Provider lives at the app root; aborts shouldn't happen in
            // steady state. If they do (e.g. strict-mode initial cycle),
            // leave the cache un-stamped so the next call retries.
            return entry.value;
          }
          if (response.kind === "ERROR") {
            console.error("Feature flag error:", response.errorKind);
            entry.value = false;
          } else {
            entry.value = response.value ?? false;
          }
          entry.fetchedAt = Date.now();
          entry.subscribers.forEach((listener) => listener(entry.value));
          return entry.value;
        } catch (err) {
          console.error("Error fetching feature flag:", err);
          entry.value = false;
          entry.fetchedAt = Date.now();
          entry.subscribers.forEach((listener) => listener(entry.value));
          return false;
        } finally {
          entry.isLoading = false;
          entry.promise = undefined;
        }
      })();
      entry.promise = promise;
      return promise;
    },
    [mutate, ttlMs, authReady]
  );

  const subscribe = useCallback(
    (name: string, ctx: TargetedFlagContext | undefined, listener: (v: boolean) => void) => {
      const key = targetedCacheKey(name, ctx);
      const entry = getOrCreateEntry(cacheRef.current, key);
      entry.subscribers.add(listener);
      return () => {
        entry.subscribers.delete(listener);
      };
    },
    []
  );

  const getEntry = useCallback(
    (name: string, ctx?: TargetedFlagContext) => cacheRef.current.get(targetedCacheKey(name, ctx)),
    []
  );

  const value = useMemo<TargetedFlagsContextValue>(
    () => ({ evaluate, subscribe, getEntry, ttlMs }),
    [evaluate, subscribe, getEntry, ttlMs]
  );

  return <TargetedFlagsContext.Provider value={value}>{children}</TargetedFlagsContext.Provider>;
};

const targetedWarnedFlags = new Set<string>();
function warnIfNoTargetedProvider(name: string, value: TargetedFlagsContextValue | null) {
  if (process.env.NODE_ENV === "production" || value !== null || targetedWarnedFlags.has(name)) {
    return;
  }
  targetedWarnedFlags.add(name);
  console.warn(
    `useLaunchDarklyTargetedFlag("${name}") called without a LaunchDarklyTargetedFlagsProvider in the tree. ` +
      `Returning false. Mount LaunchDarklyTargetedFlagsProvider at your app root.`
  );
}

/**
 * Reads a single per-context LaunchDarkly flag. Resolves via the
 * `LaunchDarklyTargetedFlagsProvider` mounted at the app root.
 */
export function useLaunchDarklyTargetedFlag(
  flagName: string,
  context?: TargetedFlagContext
): { featureFlagEnabled: boolean; isLoading: boolean } {
  const ctxValue = useContext(TargetedFlagsContext);
  warnIfNoTargetedProvider(flagName, ctxValue);

  const cacheKey = ctxValue ? targetedCacheKey(flagName, context) : "";

  const [state, setState] = useState<{ value: boolean; isLoading: boolean }>(() => {
    if (!ctxValue) {
      return { value: false, isLoading: false };
    }
    const entry = ctxValue.getEntry(flagName, context);
    if (entry && !entry.isLoading && entry.fetchedAt > 0 && Date.now() - entry.fetchedAt < ctxValue.ttlMs) {
      return { value: entry.value, isLoading: false };
    }
    return { value: false, isLoading: true };
  });

  useEffect(() => {
    if (!ctxValue) {
      return;
    }
    const unsubscribe = ctxValue.subscribe(flagName, context, (v) => {
      setState({ value: v, isLoading: false });
    });
    let cancelled = false;
    ctxValue.evaluate(flagName, context).then((v) => {
      if (!cancelled) {
        setState({ value: v, isLoading: false });
      }
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
    // context object identity changes every render at most call sites; cacheKey captures the relevant content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey, ctxValue, flagName]);

  return { featureFlagEnabled: state.value, isLoading: state.isLoading };
}
